import type { AppRuntimeErrorKey } from '@/i18n/ns/app-runtime-errors';

// The sentence an App shows for a refused or failed live-data operation. The host attaches it to the SDK error as
// `message` next to the typed `code`, so App code that prints an error prints words, never "[object Object]".
// Codes group into a few kinds the person can act on; a code not listed here reads as "couldn't load".
const KIND: Readonly<Record<string, AppRuntimeErrorKey>> = {
  // The SDK wire's own codes (operation-client.ts).
  NOT_AUTHORIZED: 'access',
  INVALID_INPUT: 'invalid',
  LIMIT_EXCEEDED: 'tooLarge',
  UNAVAILABLE: 'unavailable',
  // The connected source behind the view.
  resource_connection_unavailable: 'connection',
  connection_required: 'connection',
  connection_write_unavailable: 'connection',
  resource_connection_changed: 'connectionChanged',
  connection_binding_stale: 'connectionChanged',
  connection_changed: 'connectionChanged',
  read_access_stale: 'connectionChanged',
  connection_unavailable: 'connection',
  connection_not_found: 'connection',
  source_binding_stale: 'connectionChanged',
  // The reader's current access to the App or its data.
  app_not_authorized: 'access',
  not_authorized: 'access',
  read_access_denied: 'access',
  read_operation_denied: 'access',
  company_required: 'access',
  view_cache_scope_denied: 'access',
  app_version_not_authorized: 'access',
  app_policy_changed: 'access',
  runtime_access_denied: 'access',
  collection_not_authorized: 'access',
  collection_denied: 'access',
  operation_denied: 'access',
  app_web_credentials_denied: 'access',
  preview_not_authorized: 'access',
  chat_image_not_authorized: 'access',
  // The App itself changed under the open view.
  app_saved_selection_changed: 'appChanged',
  view_cache_selection_changed: 'appChanged',
  app_collection_inventory_changed: 'appChanged',
  operation_digest_mismatch: 'appChanged',
  scope_invalidated: 'appChanged',
  query_timeout: 'slow',
  app_web_rate_limited: 'busy',
  app_web_invalid_request: 'invalid',
  app_web_response_bounds: 'tooLarge',
};

export function appRuntimeErrorKind(code: unknown): AppRuntimeErrorKey {
  return (typeof code === 'string' && Object.hasOwn(KIND, code) && KIND[code]) || 'unavailable';
}

export function appRuntimeErrorMessage(
  code: unknown,
  copy: Readonly<Record<AppRuntimeErrorKey, string>>,
): string {
  return copy[appRuntimeErrorKind(code)];
}
